import { memo, useCallback, useState } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { borderRadius, motion } from '@tabbio/tokens';
import { useTheme } from '../../hooks/useTheme';
import { useLocale } from '../../hooks/useLocale';

export type SegmentedTab = { key: string; label: string };

interface SegmentedTabsProps {
  segments: SegmentedTab[];
  value: string;
  onChange: (key: string) => void;
  style?: StyleProp<ViewStyle>;
}

// Web variant of the shared segmented control: CSS transitions, hover, focus
// ring, and pointer cursor (the native file handles haptics + press scale).
function SegmentedTabsImpl({ segments, value, onChange, style }: SegmentedTabsProps) {
  const { theme } = useTheme();
  const { isRTL } = useLocale();
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  // Also update RN Web's locale context so logical divider edges resolve RTL.
  const directionProps = { dir: isRTL ? 'rtl' : 'ltr' };

  const handlePress = useCallback(
    (key: string) => {
      if (key === value) return;
      onChange(key);
    },
    [onChange, value],
  );

  return (
    <View
      {...directionProps}
      accessibilityRole="tablist"
      style={[
        styles.track,
        {
          backgroundColor: theme.surfaceSecondary,
          direction: isRTL ? 'rtl' : 'ltr',
          flexDirection: 'row',
        },
        style,
      ]}
    >
      {segments.map((seg, index) => {
        const active = seg.key === value;
        const prevActive = index > 0 && segments[index - 1]?.key === value;
        const showDivider = index > 0 && !active && !prevActive;
        const hovered = hoveredKey === seg.key;

        return (
          <Pressable
            key={seg.key}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
            focusable
            onPress={() => handlePress(seg.key)}
            onHoverIn={() => setHoveredKey(seg.key)}
            onHoverOut={() => setHoveredKey((current) => (current === seg.key ? null : current))}
            style={[
              styles.segment,
              {
                backgroundColor: active
                  ? theme.text
                  : hovered
                    ? theme.buttonGhostHover
                    : 'transparent',
                boxShadow: active ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
                transitionProperty: 'background-color, color',
                transitionDuration: `${motion.duration.fast}ms`,
                transitionTimingFunction: motion.easing.enter,
                outlineColor: theme.focusRing,
                outlineOffset: 2,
                cursor: active ? 'default' : 'pointer',
              } as ViewStyle,
            ]}
          >
            {showDivider ? (
              <View style={[styles.divider, { backgroundColor: theme.borderStrong }]} />
            ) : null}
            <Text
              numberOfLines={1}
              style={[
                styles.label,
                { color: active ? theme.background : theme.textSecondary },
              ]}
            >
              {seg.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

export const SegmentedTabs = memo(SegmentedTabsImpl);

const styles = StyleSheet.create({
  track: {
    borderRadius: borderRadius.full,
    padding: 4,
    alignItems: 'stretch',
  },
  segment: {
    flex: 1,
    minHeight: 38,
    borderRadius: borderRadius.full,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  divider: {
    position: 'absolute',
    insetInlineStart: 0,
    top: '24%',
    height: '52%',
    width: 1,
  } as ViewStyle,
  label: {
    fontSize: 14,
    fontWeight: '600',
  },
});
